import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, setDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import {
  formatDateTime,
  formatKes,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel,
  toDateValue
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';
import './directExpenses.css';

const EXPENSE_CATEGORIES = [
  'Office supplies',
  'Transport',
  'Repairs and maintenance',
  'Utilities',
  'Food and refreshments',
  'Communication',
  'Equipment',
  'Other'
];

const PAYMENT_METHODS = ['Cash', 'MPESA', 'Card', 'Bank Transfer', 'Cheque', 'Other'];

const buildEmptyExpenseForm = () => ({
  title: '',
  category: EXPENSE_CATEGORIES[0],
  description: '',
  amount: '',
  expenseDate: getDateTimeInputValue(),
  campus: 'General',
  payee: '',
  paymentMethod: 'Cash',
  reference: ''
});

function DirectExpensesComp() {
  const auth = getAuth();
  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [form, setForm] = useState(buildEmptyExpenseForm());

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'Expenses'),
      (snapshot) => {
        setExpenses(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Failed to subscribe to direct expenses:', error);
        setLoading(false);
        swal('Error', 'Expenses could not be loaded right now.', 'error');
      }
    );

    getDocs(collection(db, 'Branches'))
      .then((snapshot) => setBranches(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))))
      .catch((error) => console.error('Failed to load expense campuses:', error));

    return unsubscribe;
  }, []);

  const branchesById = useMemo(() => branches.reduce((result, branch) => {
    result[branch.id] = branch.branchName || branch.name || 'Campus';
    return result;
  }, {}), [branches]);

  const periodExpenses = useMemo(() => expenses
    .filter((expense) => {
      const date = toDateValue(expense.expenseDate)
        || toDateValue(expense.timeStamp)
        || toDateValue(expense.createdAt);
      return getMonthKey(date) === periodKey;
    })
    .sort((first, second) => {
      const firstDate = toDateValue(first.expenseDate || first.timeStamp || first.createdAt)?.getTime() || 0;
      const secondDate = toDateValue(second.expenseDate || second.timeStamp || second.createdAt)?.getTime() || 0;
      return secondDate - firstDate;
    }), [expenses, periodKey]);

  const filteredExpenses = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return periodExpenses;

    return periodExpenses.filter((expense) => {
      const campus = expense.campus === 'General'
        ? 'General'
        : expense.campusName || branchesById[expense.campus] || expense.campus || '';
      return [
        expense.expenseTitle,
        expense.expenseType,
        expense.expenseDesc,
        expense.payee,
        expense.paymentMethod,
        expense.reference,
        campus
      ].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [branchesById, periodExpenses, searchTerm]);

  const periodTotal = useMemo(() => periodExpenses.reduce(
    (total, expense) => total + Number(expense.expenseAmount || expense.amount || 0),
    0
  ), [periodExpenses]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const openModal = () => {
    setForm(buildEmptyExpenseForm());
    setModalShow(true);
  };

  const closeModal = () => {
    if (!submitting) setModalShow(false);
  };

  const handleSubmit = async () => {
    const title = form.title.trim();
    const amount = Number(form.amount);
    const expenseDate = new Date(form.expenseDate);
    if (!title) {
      swal('Add an expense name', 'Enter what was purchased or paid for.', 'warning');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      swal('Invalid amount', 'Enter an expense amount greater than zero.', 'warning');
      return;
    }
    if (Number.isNaN(expenseDate.getTime())) {
      swal('Invalid date', 'Choose a valid expense date.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before recording an expense.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const expenseRef = doc(collection(db, 'Expenses'));
      const now = Timestamp.now();
      const campusName = form.campus === 'General' ? 'General' : branchesById[form.campus] || '';
      const payload = {
        expenseTitle: title,
        expenseType: form.category,
        expenseDesc: form.description.trim(),
        expenseAmount: amount,
        expenseDate: Timestamp.fromDate(expenseDate),
        timeStamp: now,
        campus: form.campus,
        campusName,
        payee: form.payee.trim(),
        paymentMethod: form.paymentMethod,
        reference: form.reference.trim(),
        paymentStatus: 'Paid',
        status: 'Paid',
        sourceType: 'Direct expense',
        sourcePage: 'direct-expenses',
        createdAt: now,
        createdBy: currentUser.uid,
        docId: expenseRef.id
      };

      await setDoc(expenseRef, payload);
      setExpenses((currentExpenses) => [
        { id: expenseRef.id, ...payload },
        ...currentExpenses.filter((expense) => expense.id !== expenseRef.id)
      ]);
      await logAdminActivity({
        action: 'Recorded Direct Expense',
        details: `Recorded ${formatKes(amount)} for ${title}`,
        meta: {
          expenseId: expenseRef.id,
          expenseTitle: title,
          expenseAmount: amount,
          expenseCategory: form.category,
          campus: campusName,
          sourcePage: 'direct-expenses'
        }
      });

      setModalShow(false);
      swal('Expense recorded', `${title} was recorded as a paid expense.`, 'success');
    } catch (error) {
      console.error('Failed to record direct expense:', error);
      swal('Error', error.message || 'The expense could not be recorded.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="teamsComponent directExpensesPage">
      <header className="directExpensesHeader">
        <div>
          <span className="directExpensesEyebrow">Finance / Expenses</span>
          <h1>Direct expenses</h1>
          <p>Record small purchases already paid without a requisition.</p>
        </div>
        <button type="button" className="directExpensesAddButton" onClick={openModal}>
          <FontAwesomeIcon icon={faPlus} /> <span>Record expense</span>
        </button>
      </header>

      <section className="directExpensesOverview" aria-label="Expense summary">
        <div className="directExpensesMetric">
          <span>{getMonthLabel(periodKey)} spend</span>
          <strong>{formatKes(periodTotal)}</strong>
        </div>
        <div className="directExpensesMetric">
          <span>Recorded expenses</span>
          <strong>{periodExpenses.length}</strong>
        </div>
        <div className="directExpensesPeriod">
          <Form.Label htmlFor="direct-expenses-period">Month</Form.Label>
          <Form.Control
            id="direct-expenses-period"
            type="month"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
          />
        </div>
      </section>

      <section className="directExpensesRegister">
        <div className="directExpensesRegisterHeader">
          <div>
            <h2>Expense register</h2>
            <p>These paid entries are included in finance reports.</p>
          </div>
          <Form.Control
            className="directExpensesSearch"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search expenses"
            aria-label="Search expenses"
          />
        </div>

        {loading ? (
          <div className="directExpensesState"><Spinner animation="border" role="status" /><span>Loading expenses...</span></div>
        ) : filteredExpenses.length === 0 ? (
          <div className="directExpensesEmpty">
            <FontAwesomeIcon icon={faReceipt} />
            <h3>{searchTerm ? 'No matching expenses' : `No expenses in ${getMonthLabel(periodKey)}`}</h3>
            <p>{searchTerm ? 'Try a different search.' : 'Record a direct purchase to start the register.'}</p>
          </div>
        ) : (
          <div className="directExpensesTableWrap">
            <table className="directExpensesTable">
              <thead>
                <tr>
                  <th>Expense</th>
                  <th>Campus</th>
                  <th>Paid via</th>
                  <th>Date</th>
                  <th className="directExpensesAmountCol">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense) => {
                  const campus = expense.campus === 'General'
                    ? 'General'
                    : expense.campusName || branchesById[expense.campus] || expense.campus || 'Campus not set';
                  return (
                    <tr key={expense.id}>
                      <td data-label="Expense">
                        <div className="directExpensesItem">
                          <strong>{expense.expenseTitle || expense.expenseType || 'Expense'}</strong>
                          <span>{[expense.expenseType || 'Uncategorised', expense.payee, expense.expenseDesc].filter(Boolean).join(' · ')}</span>
                        </div>
                      </td>
                      <td data-label="Campus">{campus}</td>
                      <td data-label="Paid via">{expense.paymentMethod || '—'}</td>
                      <td data-label="Date">{formatDateTime(expense.expenseDate || expense.timeStamp || expense.createdAt)}</td>
                      <td data-label="Amount" className="directExpensesAmountCol">{formatKes(expense.expenseAmount || expense.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal show={modalShow} onHide={closeModal} centered contentClassName="directExpensesModal">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Record direct expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">Use this for an expense that has already been paid without a requisition.</p>
          <Form.Group className="mb-3">
            <Form.Label>What was purchased or paid for?</Form.Label>
            <Form.Control name="title" value={form.title} onChange={handleFormChange} placeholder="For example: Batteries" disabled={submitting} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select name="category" value={form.category} onChange={handleFormChange} disabled={submitting}>
              {EXPENSE_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description <span className="text-muted">(optional)</span></Form.Label>
            <Form.Control as="textarea" rows={2} name="description" value={form.description} onChange={handleFormChange} placeholder="Add details" disabled={submitting} />
          </Form.Group>
          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Amount (KES)</Form.Label>
              <Form.Control type="number" min="0.01" step="0.01" name="amount" value={form.amount} onChange={handleFormChange} placeholder="0" disabled={submitting} />
            </Form.Group>
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Expense date</Form.Label>
              <Form.Control type="datetime-local" name="expenseDate" value={form.expenseDate} onChange={handleFormChange} disabled={submitting} />
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Campus</Form.Label>
              <Form.Select name="campus" value={form.campus} onChange={handleFormChange} disabled={submitting}>
                <option value="General">General</option>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branchName || branch.name || branch.id}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Payment method</Form.Label>
              <Form.Select name="paymentMethod" value={form.paymentMethod} onChange={handleFormChange} disabled={submitting}>
                {PAYMENT_METHODS.map((method) => <option key={method} value={method}>{method}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="col-md-6 mb-0">
              <Form.Label>Paid to <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control name="payee" value={form.payee} onChange={handleFormChange} placeholder="Shop or recipient" disabled={submitting} />
            </Form.Group>
            <Form.Group className="col-md-6 mb-0">
              <Form.Label>Receipt or reference <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control name="reference" value={form.reference} onChange={handleFormChange} placeholder="Receipt number" disabled={submitting} />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button variant="dark" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><Spinner as="span" animation="border" size="sm" /> Saving...</> : 'Save expense'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DirectExpensesComp;
